import {
    forwardRef,
    useEffect,
    useId,
    useState,
    type ChangeEvent,
    type FocusEvent,
    type InputHTMLAttributes,
    type KeyboardEvent
} from 'react';
import { formStyles } from '@utils/formStyles';
import { clampNumber, parseNumericDraft, roundToStepPrecision, sanitizeNumericDraft, toNumericDraft } from './numericDraft';

type OmittedNativeProps = 'value' | 'defaultValue' | 'onChange' | 'type' | 'min' | 'max' | 'step' | 'inputMode';

export interface FormNumberInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, OmittedNativeProps> {
    label?: string;
    error?: string;
    helpText?: string;
    fullWidth?: boolean;
    noMargin?: boolean;
    /** Unit adornment rendered inside the input's right edge, e.g. "%" or "days" */
    suffix?: string;
    /** Adornment rendered inside the input's left edge, e.g. "$" */
    prefix?: string;
    /** Committed value. `null`/`undefined` renders an empty field. */
    value: number | null | undefined;
    /** Receives the parsed number, or `null` while the field is empty. */
    onValueChange: (value: number | null) => void;
    min?: number;
    max?: number;
    /** Arrow-key increment (default 1). Also sets the rounding precision for stepping. */
    step?: number;
    /** Restrict entry to whole numbers. */
    integer?: boolean;
    /** Allow a leading minus sign. Defaults to allowed unless `min` is zero or greater. */
    allowNegative?: boolean;
}

/**
 * Numeric text field that behaves the way users expect: the field can be emptied, typing over a
 * `0` does not leave a stray leading zero, and pasted values like "$1,200.50" are accepted.
 *
 * Prefer this over `<FormInput type="number" />` for every numeric field. Parents must hold
 * `number | null` so an empty field can round-trip as `null` instead of snapping back to `0`.
 * Values are clamped to `min`/`max` on blur rather than mid-keystroke, so intermediate text
 * ("1.", "-") is never rewritten while typing.
 */
const FormNumberInput = forwardRef<HTMLInputElement, FormNumberInputProps>(
    (
        {
            label,
            error,
            helpText,
            fullWidth = true,
            noMargin = false,
            prefix,
            suffix,
            value,
            onValueChange,
            min,
            max,
            step = 1,
            integer = false,
            allowNegative,
            className = '',
            id,
            onBlur,
            onFocus,
            onKeyDown,
            ...rest
        },
        ref
    ) => {
        const generatedId = useId();
        const inputId = id ?? generatedId;
        const [draft, setDraft] = useState(() => toNumericDraft(value));
        const [isFocused, setIsFocused] = useState(false);

        // While the field has focus the draft belongs to the user, so an empty field stays empty
        // even if the parent stores a fallback. Outside of editing the committed value wins, which
        // keeps external resets, row switches, and clamped values in sync with what is displayed.
        useEffect(() => {
            if (!isFocused) setDraft(toNumericDraft(value));
        }, [isFocused, value]);

        const negativeAllowed = allowNegative ?? (min === undefined || min < 0);

        const commitDraft = (nextDraft: string) => {
            setDraft(nextDraft);
            onValueChange(parseNumericDraft(nextDraft));
        };

        const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
            commitDraft(sanitizeNumericDraft(event.target.value, { integer, allowNegative: negativeAllowed }));
        };

        const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
            setIsFocused(true);
            onFocus?.(event);
        };

        const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
            const parsed = parseNumericDraft(draft);
            const normalized = parsed === null ? null : clampNumber(parsed, min, max);

            setDraft(toNumericDraft(normalized));
            setIsFocused(false);

            if (normalized !== (value ?? null)) onValueChange(normalized);

            onBlur?.(event);
        };

        const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
            onKeyDown?.(event);

            if (event.defaultPrevented || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) return;

            event.preventDefault();

            const direction = event.key === 'ArrowUp' ? 1 : -1;
            const base = parseNumericDraft(draft) ?? clampNumber(0, min, max);
            const stepped = roundToStepPrecision(base + direction * step, step);

            commitDraft(toNumericDraft(clampNumber(stepped, min, max)));
        };

        const inputClasses = [
            error ? formStyles.inputError : formStyles.input,
            formStyles.placeholder,
            prefix ? 'pl-8' : '',
            suffix ? 'pr-10' : '',
            className
        ]
            .filter(Boolean)
            .join(' ');

        let containerClasses;

        if (noMargin) {
            containerClasses = fullWidth ? formStyles.fullWidthContainerNoMargin : formStyles.containerNoMargin;
        } else {
            containerClasses = fullWidth ? formStyles.fullWidthContainer : formStyles.container;
        }

        const inputElement = (
            <input
                autoComplete="off"
                className={inputClasses}
                id={inputId}
                inputMode={integer && !negativeAllowed ? 'numeric' : 'decimal'}
                ref={ref}
                type="text"
                value={draft}
                onBlur={handleBlur}
                onChange={handleChange}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                {...rest}
            />
        );

        return (
            <div className={containerClasses}>
                {label && (
                    <label className={formStyles.label} htmlFor={inputId}>
                        {label}
                    </label>
                )}
                {prefix || suffix ? (
                    <div className="relative">
                        {prefix && (
                            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-zinc-300">
                                {prefix}
                            </span>
                        )}
                        {inputElement}
                        {suffix && (
                            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-zinc-500">
                                {suffix}
                            </span>
                        )}
                    </div>
                ) : (
                    inputElement
                )}
                {helpText && <div className={formStyles.helpText}>{helpText}</div>}
                {error && <div className={formStyles.errorText}>{error}</div>}
            </div>
        );
    }
);

FormNumberInput.displayName = 'FormNumberInput';

export default FormNumberInput;
